import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import { toast } from 'react-toastify';
import routes from '../../../Routes/routes';
import { useDispatch, useSelector } from 'react-redux';
import { getDepartments } from '../../../reduxToolkit/CompanyEmployees/CompanyEmployeesApi';
import { GetAllServiceType } from '../../../reduxToolkit/ServiceType/ServiceTypeApi';
import HandleChange from '../../../Helpers/HandleChange';
import { PHOTO_PICKER_MODEL } from '../../../components/PhotoPicker/SubComponent/Models/Models';
import { FIELD_TYPE, FIELD_TYPES_MODEL } from '../../Dashboard/Visitor/Type/Model/Model';
import { DOCUMENTATION_APPROVAL_FLOW_MODEL, LEVEL_MODEL } from "../Model/Model";
import {CONTRACTORS_SUBVIEW_MODEL, TABS_CONTRACTORS} from "../../Dashboard/Contractors/Model/Models";

import { errorMsg } from "../../../Apis/functions";
import {USER_TYPE_MODEL} from "../../../enums/userTypeEnum";
import {permissionObj} from "../../../Helpers/permission";
import {
    CreateDocumentationApprovalFlow,
    GetDocumentApprovalFlowById, UpdateDocumentationApprovalFlow
} from "../../../reduxToolkit/DocumentationApproval/DocumentationApprovalApi";
import {PROVIDER_SUB_VIEW_MODEL, TABS_PROVIDER} from "../../Provider/Model/Model";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 */

/**
 * Custom hook that manages the creation and update logic for a Documentation Approval Flow.
 * It centralizes state handling, validation, file management, navigation, and API calls.
 *
 * @function CreateUpdateDocumentationApprovalFlowApp
 *
 * @param {number} [userTypeId=USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE]
 *        User type used to determine routing and visibility rules.
 *
 */
const CreateUpdateDocumentationApprovalFlowApp = ({userTypeId = USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE}) => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);

    const detailsRoute = userTypeId === USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE
        ? routes.contractor.detailsApprovalLevel
        : routes.suppliers.detailsApprovalLevel;

    // The flow is opened as read only
    const isDetails = location?.pathname?.includes(detailsRoute);

    // Creating a flow requires the create permission, editing an existing one requires update
    const createPermission = userTypeId === USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE
        ? permissionObj?.WEB_CONTRACTOR_APPROVAL_FLOW_CREATE
        : permissionObj?.WEB_SUPPLIER_APPROVAL_FLOW_CREATE;

    const updatePermission = userTypeId === USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE
        ? permissionObj?.WEB_CONTRACTOR_APPROVAL_FLOW_UPDATE
        : permissionObj?.WEB_SUPPLIER_APPROVAL_FLOW_UPDATE;

    const canEdit = !isDetails && (DEV || permission?.includes(id ? updatePermission : createPermission));

    const [emptyFields, setEmptyFields] = useState(false);
    const [approvalFlow, setApprovalFlow] = useState({ ...DOCUMENTATION_APPROVAL_FLOW_MODEL });
    const [tempLevel, setTempLevel] = useState({ ...LEVEL_MODEL });
    const [photoPicker, setPhotoPicker] = useState({ ...PHOTO_PICKER_MODEL });

    const { handleChange: handleChangeFlow } = HandleChange({ model: approvalFlow, setModel: setApprovalFlow });
    const { handleChange: handleChangeTemp } = HandleChange({ model: tempLevel, setModel: setTempLevel });

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedLevelId, setSelectedLevelId] = useState(null);

    const getAllDepartments = useSelector((state) => state?.CompanyEmployeesSlice?.employeeDepartments);
    const allServiceType = useSelector((state) => state?.ServiceTypeSlice?.getAllServiceType);
    const fieldTypes = FIELD_TYPES_MODEL().filter((item) => item.id === FIELD_TYPE.TEXT.name || item.id === FIELD_TYPE.DOCUMENT.name);

    const allRequiredFilled = DOCUMENTATION_APPROVAL_FLOW_MODEL.isValid.call({
        ...approvalFlow,
        levels: approvalFlow.levels
    });

    const tempLevelIsValid = LEVEL_MODEL.isValid.call(tempLevel);

    useEffect(() => {
        dispatch(getDepartments());
        dispatch(GetAllServiceType());
    }, [dispatch]);

    useEffect(() => {
        if (id) {
            dispatch(GetDocumentApprovalFlowById(id))
                .then(({ payload: { data:{data, success, code} } }) => {
                    if (success) {
                        const flow = DOCUMENTATION_APPROVAL_FLOW_MODEL.set(data);
                        setApprovalFlow(flow);
                    } else {
                        errorMsg(code);
                    }
                });
        }
    }, [id]);

    /**
     * Builds a level object from the tempLevel form data, assigning a temporary ID and optional file metadata.
     *
     * @function buildLevelFromForm
     * @param {number} index - Level index number.
     * @returns {Object} A formatted level object ready for insertion.
     */
    const buildLevelFromForm = (index) => {
        const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`;

        const baseLevel = {
            id : tempId,
            name: tempLevel.name,
            levelNumber: index,
            department: tempLevel.department,
            type: tempLevel.type,
            description: tempLevel.description,
        };

        const file = photoPicker?.fileSelected;

        if (file) {
            return {
                ...baseLevel,
                fileKey: `files[${file.name}_${index}]`,
                file: file,
                fileName: `files[${file.name}_${index}]`,
            };
        }

        return baseLevel;
    };

    /**
     * Reassigns sequential level numbers starting from 1.
     *
     * @function reorderLevels
     * @param {Array<Object>} levels - Array of level objects.
     * @returns {Array<Object>} The reordered level array.
     */
    const reorderLevels = (levels) => {
        return levels.map((l, i) => ({
            ...l,
            levelNumber: i + 1
        }));
    };

    /**
     * Validates temporary level data, creates a new level, adds it to the list and resets the temp form.
     *
     * @function handleAddDocumentationLevel
     * @returns {void}
     */
    const handleAddDocumentationLevel = () => {
        if (!tempLevelIsValid) {
            toast.warn(t('please_fill_all_fields'));
            return;
        }

        const newLevelRaw = buildLevelFromForm(approvalFlow.levels.length + 1);
        const newLevel = LEVEL_MODEL.set(newLevelRaw);

        setApprovalFlow(prev => ({
            ...prev,
            levels: reorderLevels([...prev.levels, newLevel])
        }));

        setTempLevel({ ...LEVEL_MODEL });
        setPhotoPicker({ ...PHOTO_PICKER_MODEL });
        setEmptyFields(false);
    };

    /**
     * Removes a specific level by ID, then reorders remaining levels.
     *
     * @function handleDeleteLevel
     * @param {string} levelId - Identifier of the level to delete.
     * @returns {void}
     */
    const handleDeleteLevel = (levelId) => {
        setApprovalFlow(prev => ({
            ...prev,
            levels: reorderLevels(prev.levels.filter(l => l.id !== levelId))
        }));
    };

    /**
     * Constructs FormData and sends the API request to create a documentation approval flow.
     *
     * @function handleCreate
     * @returns {void}
     */
    const handleCreate = () => {
        if (!DOCUMENTATION_APPROVAL_FLOW_MODEL.isValid.call(approvalFlow)) {
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
            return;
        }

        const dto = DOCUMENTATION_APPROVAL_FLOW_MODEL.toCreateUpdate.call(approvalFlow);
        const payload = {
            ...dto,
            userType: userTypeId,
        };
        const formData = new FormData();
        const jsonBlob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        formData.append('data', jsonBlob);

        approvalFlow.levels.forEach(level => {
            if (level.file && level.fileKey) {
                formData.append(level.fileKey, level.file);
            }
        });

        dispatch(CreateDocumentationApprovalFlow(formData)).then(({payload:{data:{success, code}}})=>{
            if (success){
                toast.success(t('has_been_created_successfully'));
                handleOnBack();
            }else errorMsg(code)
        });
    };

    /**
     * Updates an existing documentation approval flow.
     * Handles new file uploads and persists existing ones.
     *
     * @function handleUpdate
     * @returns {void}
     */
    const handleUpdate = () => {
        if (!DOCUMENTATION_APPROVAL_FLOW_MODEL.isValid.call(approvalFlow)) {
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
            return;
        }

        const processedLevels = approvalFlow.levels.map(level => {
            const hasNewFile = level.file && level.fileKey;

            if (!hasNewFile) {
                return {
                    ...level,
                    fileUploadDto: level.fileUploadDto
                };
            }

            return {
                ...level,
                fileUploadDto: {
                    id: null,
                    userFileNames: [level.file.name],
                    files: 1,
                    size: level.file.size,
                    path: null,
                    filePath: null
                }
            };
        });

        const dto = DOCUMENTATION_APPROVAL_FLOW_MODEL.toCreateUpdate.call({
            ...approvalFlow,
            levels: processedLevels
        });

        const payload = {
            ...dto,
            userType: userTypeId,
        };

        const formData = new FormData();
        const jsonBlob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        formData.append('data', jsonBlob);

        approvalFlow.levels.forEach(level => {
            if (level.file && level.fileKey) {
                formData.append(level.fileKey, level.file);
            }
        });

        dispatch(UpdateDocumentationApprovalFlow(formData))
            .then(({ payload: { data: { data, success, code } } }) => {
                if (success) {
                    const route = userTypeId === USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE
                        ? routes.contractor.updateApprovalLevel
                        : routes.suppliers.updateApprovalLevel;

                    navigate(route + data.id, { replace: true });
                    toast.success(t('has_been_updated_successfully'));
                } else {
                    errorMsg(code);
                }
            });
    };

    /**
     * Determines whether to create or update based on URL param `id`.
     *
     * @function handleAction
     * @returns {void}
     */
    const handleAction = () => (id ? handleUpdate() : handleCreate());


    /**
     * Navigates user back to the Contractor or Provider list screen depending on user type.
     *
     * @function handleOnBack
     * @returns {void}
     */
    const handleOnBack = () => {

        const tab = userTypeId === USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE
            ? TABS_CONTRACTORS.DOCUMENTATION.INDEX
            : TABS_PROVIDER.DOCUMENTATION.INDEX

        const subView = userTypeId === USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE
            ? CONTRACTORS_SUBVIEW_MODEL.DOCUMENTATION.APPROVAL_FLOW
            : PROVIDER_SUB_VIEW_MODEL.DOCUMENTATION.APPROVAL_FLOW

        const route = userTypeId === USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE
            ? routes.contractor.list
            : routes.suppliers.main

        navigate(route, {
            state: {
                currentTab: tab,
                currentSubView: subView,
            },
        });
    };


    /**
     * Resets form fields, temporary level data, and file inputs.
     *
     * @function resetForm
     * @returns {void}
     */
    const resetForm = () => {
        setApprovalFlow({ ...DOCUMENTATION_APPROVAL_FLOW_MODEL });
        setTempLevel({ ...LEVEL_MODEL });
        setPhotoPicker({ ...PHOTO_PICKER_MODEL });
    };

    return {
        t,
        id,
        emptyFields,
        approvalFlow,
        setApprovalFlow,
        tempLevel,
        handleChangeTemp,
        handleChangeFlow,
        photoPicker,
        setPhotoPicker,
        getAllDepartments,
        showDeleteModal,
        setShowDeleteModal,
        selectedLevelId,
        setSelectedLevelId,
        allServiceType,
        fieldTypes,
        allRequiredFilled,
        tempLevelIsValid,
        handleAddDocumentationLevel,
        handleDeleteLevel,
        handleCreate,
        handleUpdate,
        resetForm,
        handleOnBack,
        handleAction,
        setEmptyFields,
        isDetails,
        canEdit
    };
};

export default CreateUpdateDocumentationApprovalFlowApp;
